import { useEffect, useState, useCallback } from 'react'
import client from '../api/client'

interface InstallPromptState {
  canInstall: boolean
  isIOS: boolean
  isInstalled: boolean
  isDismissed: boolean
  promptInstall: () => Promise<void>
  dismissPrompt: () => void
}

// Bump the version suffix when the iOS hint copy changes so users who
// dismissed an older version see the updated instructions once.
const DISMISSED_KEY = 'pwa_install_dismissed_v5'

function isIOSDevice(): boolean {
  if (typeof window === 'undefined') return false
  // @ts-ignore — MSStream not in types
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-ignore — iOS Safari extension on Navigator
    window.navigator.standalone === true
  )
}

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(isStandalone())
  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') return false
    return localStorage.getItem(DISMISSED_KEY) === '1'
  })

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      e.preventDefault()
      setDeferredPrompt(e)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    const handler = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
      // Track on backend (best-effort, ignore failures).
      client.post('/api/me/pwa-installed/').catch(() => {})
    }
    window.addEventListener('appinstalled', handler)
    return () => window.removeEventListener('appinstalled', handler)
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    if (choice.outcome === 'accepted') {
      setIsInstalled(true)
    }
    setDeferredPrompt(null)
  }, [deferredPrompt])

  const dismissPrompt = useCallback(() => {
    setIsDismissed(true)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(DISMISSED_KEY, '1')
    }
  }, [])

  return {
    canInstall: !!deferredPrompt && !isInstalled && !isDismissed,
    isIOS: isIOSDevice() && !isInstalled && !isDismissed,
    isInstalled,
    isDismissed,
    promptInstall,
    dismissPrompt,
  }
}
